import { useCallback, useRef } from 'react';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import {
  ScreeningCorrectionRequest,
  ScreeningCorrectionState,
  ScreeningResult,
} from '@marageti/z4-sdk/lib/travel';

export type CorrectionStatus = 'idle' | 'loading' | 'ready' | 'error';

const useScreeningCorrection = (
  screeningResult?: ScreeningResult,
  onLoadError?: (retry: () => void) => void,
) => {
  const apiClient = useApiClient();
  const retryRef = useRef<() => void>(() => undefined);
  const screeningResultId = screeningResult?.id;
  const screeningResultUpdateId = screeningResult?.updateId;
  const canFetch = Boolean(screeningResultId && screeningResultUpdateId);

  const correctionState = useFetch<ScreeningCorrectionState>(
    apiClient.screeningResultsClient.getScreeningCorrection,
    [screeningResultId, screeningResultUpdateId],
    canFetch,
    {
      shouldRetryOnError: false,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (error: { code?: number }) => {
        if (error.code === 404) {
          return;
        }

        onLoadError?.(() => retryRef.current());
      },
    },
  );
  retryRef.current = () => {
    correctionState.mutate();
  };

  const saveScreeningCorrection = useCallback((
    data: ScreeningCorrectionRequest,
  ) => {
    if (!screeningResultId || !screeningResultUpdateId) {
      return Promise.reject(new Error('Missing screening result version'));
    }

    return apiClient.screeningResultsClient.putScreeningCorrection(
      screeningResultId,
      screeningResultUpdateId,
      data,
    );
  }, [apiClient, screeningResultId, screeningResultUpdateId]);

  const regenerateScreeningDraft = useCallback(() => {
    if (!screeningResultId || !screeningResultUpdateId) {
      return Promise.reject(new Error('Missing screening result version'));
    }

    return apiClient.screeningResultsClient.postScreeningDraft(
      screeningResultId,
      screeningResultUpdateId,
    );
  }, [apiClient, screeningResultId, screeningResultUpdateId]);

  let status: CorrectionStatus = 'idle';
  if (canFetch) {
    if (correctionState.isLoading) {
      status = 'loading';
    } else if (correctionState.error && correctionState.error.code !== 404) {
      status = 'error';
    } else {
      status = 'ready';
    }
  }

  return {
    status,
    data: correctionState.data,
    mutate: correctionState.mutate,
    saveScreeningCorrection,
    regenerateScreeningDraft,
  };
};

export default useScreeningCorrection;
